'use client';

import { useContext, useMemo, useState, type FC } from 'react';
import type { NotesDialogProps } from './NotesDialog.types';
import {
  Button,
  ButtonGroup,
  Flex,
  Dialog,
  Notes,
  Textarea,
} from '@/_components';
import { PREDEFINED_PROVIDED_ELSEWHERE } from '@/_constants/questions';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { addNote, deleteNote } from '@/_api/client-questionnaires';
import { UserContext } from '@/_contexts/UserProvider';
import { Question, QuestionNote } from '@/_types/models/questionnaire';
import { ToastContext } from '@/_contexts/ToastProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { PredefinedAlert } from './_components/PredefinedAlert';

export const NotesDialog: FC<NotesDialogProps> = ({ mode }) => {
  const { user, token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [note, setNote] = useState<string>('');
  const {
    selectedQuestion,
    selectedQuestions,
    setSelectedQuestions,
    setSelectedQuestion,
  } = useContext(ClientQuestionnaireContext);
  const {
    selectedPreviewQuestion,
    setSelectedPreviewQuestion,
    previewCompanyId,
    loadPreviewQuestionnaire,
  } = useContext(PreviewContext);

  const question: Question | undefined = useMemo(() => {
    if (mode === 'admin-preview') return selectedPreviewQuestion;
    if (mode === 'admin-template') return undefined;

    return selectedQuestion;
  }, [mode, selectedPreviewQuestion, selectedQuestion]);

  const companyId: number | null | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewCompanyId;
    if (user) return user.default_company_id;

    return null;
  }, [mode, previewCompanyId, user]);

  const visibleNotes: Array<QuestionNote> = useMemo(
    () =>
      question?.filtered_questionnaire_comments?.filter((n) => {
        if (mode === 'admin-preview') return !n.removed;
        if (mode === 'admin-template') return !n.removed;

        return !n.removed && !n.from_firm_user;
      }) || [],
    [mode, question],
  );

  const isOpen: boolean = !!question?.id;
  const isPredefined: boolean =
    question?.answer === PREDEFINED_PROVIDED_ELSEWHERE;

  const onDialogClose = () => {
    setSelectedQuestion(undefined);
    setSelectedPreviewQuestion(undefined);
  };

  const onDeleteNote = async (note: QuestionNote) => {
    if (!question?.id) return;

    try {
      await deleteNote({
        noteId: note?.id,
        companyId: companyId as number,
        questionId: question.id,
        token,
      });

      const updatedQuestion: Question = {
        ...question,
        filtered_questionnaire_comments: [
          ...question.filtered_questionnaire_comments.filter(
            (c) => c.id !== note.id,
          ),
        ],
      };

      const updatedQuestions = [...selectedQuestions].map((q) => {
        if (q?.id === question?.id) {
          q.filtered_questionnaire_comments =
            q.filtered_questionnaire_comments?.filter(
              (n: QuestionNote) => n?.id !== note?.id,
            );
        }

        return q;
      });

      if (mode === 'admin-preview') {
        setSelectedPreviewQuestion(updatedQuestion);
        loadPreviewQuestionnaire();
      }

      if (mode === 'client') {
        setSelectedQuestion(updatedQuestion);
        setSelectedQuestions(updatedQuestions);
      }
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onNoteSubmit = async () => {
    if (!question?.id) return addToast('error');

    try {
      const newComment = await addNote({
        questionId: question?.id!,
        text: note,
        userId: user?.id!,
        companyId: companyId as number,
        token,
      });

      const updatedQuestion: Question = {
        ...question,
        filtered_questionnaire_comments: [
          ...question.filtered_questionnaire_comments,
          newComment,
        ],
      };

      if (mode === 'admin-preview') {
        setSelectedPreviewQuestion(updatedQuestion as Question);
      }

      if (mode === 'client') {
        setSelectedQuestion(updatedQuestion as Question);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setNote('');

      if (mode === 'admin-preview') {
        loadPreviewQuestionnaire();
      }

      isPredefined && onDialogClose();
    }
  };

  const onNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setNote(event.target.value);

  return (
    <Dialog title="Notes" isOpen={isOpen} onClose={onDialogClose}>
      <Dialog.Content>
        <Flex isColumn={true} gap={10}>
          {isPredefined && <PredefinedAlert />}
          <Notes notes={visibleNotes} onDeleteNote={onDeleteNote} />
          <Flex isColumn={true} gap={8}>
            <Textarea
              rows={6}
              label="New Note"
              value={note}
              placeholder="Your message"
              onChange={onNoteChange}
            />
            <ButtonGroup align="right">
              <Button
                intent="secondary"
                onClick={onDialogClose}
                value="Cancel"
              />
              <Button
                isDisabled={!note}
                value="Send"
                intent="primary"
                onClick={onNoteSubmit}
              />
            </ButtonGroup>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
